import { isEmpty } from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { LuCigarette } from 'react-icons/lu';

import { DwhSriCategory } from '@ndvr/market-data-api';

import { Spinner } from '@/components/Spinner';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { TooltipProvider } from '@/components/ui/tooltip';
import { formatCurrencyHuman, formatPercent, useRequiredParams } from 'utils';
import { categoryIconMap } from 'views/portfolio/PortfolioPage/tabs/equity-preferences/constants';
import { useSRIRestrictions } from 'views/portfolio/PortfolioPage/tabs/equity-preferences/sri-restrictions/useSRIRestrictions';

import { type AffectedStock } from './AffectedStocksTable';
import { ExcludedStocksTab } from './ExcludedStocksTab';
import { SelectionTab } from './SelectionTab';

export interface CategoryOverride {
    shareClassId: string;
    shareId: string;
}

interface EditSRICategoryModalProps {
    isOpen: boolean;
    isSaving: boolean;
    onClose: () => void;
    category: DwhSriCategory;
    excludedSubCategories: string[];
    onSave: (
        excludedSubCategories: string[],
        overrides: CategoryOverride[],
        restrictedStocks: CategoryOverride[],
    ) => void;
}

export const EditSRICategoryModal = ({
    isOpen,
    isSaving,
    onClose,
    category,
    excludedSubCategories,
    onSave,
}: EditSRICategoryModalProps) => {
    const { accountGroupGUID } = useRequiredParams(['accountGroupGUID']);
    const { getAffectedStocksForSelection, getAllStocksForCategory, preferences, stocks } =
        useSRIRestrictions(accountGroupGUID);
    const [selectedSubCategories, setSelectedSubCategories] = useState<string[]>(excludedSubCategories);

    const currentAffectedStocks = useMemo(() => {
        return getAffectedStocksForSelection(category, selectedSubCategories);
    }, [category, selectedSubCategories, getAffectedStocksForSelection]);

    const overrides = preferences?.restrictedCategoryOverrides || [];

    // Track user's individual stock override choices for the entire modal session
    const [stockOverrides, setStockOverrides] = useState<Record<string, AffectedStock>>(() => {
        const initialAffectedStocks = getAllStocksForCategory(category);
        const overrideMap: Record<string, AffectedStock> = {};
        if (stocks) {
            initialAffectedStocks.forEach((stock) => {
                overrideMap[stock.shareClassId] = {
                    ...stock,
                    restricted: !overrides.some((override) => override.shareId === stock.shareId),
                };
            });
        }
        return overrideMap;
    });

    useEffect(() => {
        if (isEmpty(stockOverrides) && !isEmpty(stocks)) {
            const initialAffectedStocks = getAllStocksForCategory(category);
            const overrideMap: Record<string, AffectedStock> = {};
            initialAffectedStocks.forEach((stock) => {
                overrideMap[stock.shareClassId] = {
                    ...stock,
                    restricted: !overrides.some((override) => override.shareId === stock.shareId),
                };
            });
            setStockOverrides(overrideMap);
        }
    }, [stocks]);

    const restrictedStockIds = useMemo(() => {
        return currentAffectedStocks
            .filter((stock) => !stockOverrides[stock.shareClassId] || stockOverrides[stock.shareClassId]?.restricted)
            .filter((stock) => !preferences?.restrictions.some((restricted) => restricted.shareId === stock.shareId))
            .map((stock) => stock.shareClassId);
    }, [currentAffectedStocks, stockOverrides]);

    const handleStockOverrideChange = (stockId: string, excluded: boolean) => {
        setStockOverrides((prev) => ({
            ...prev,
            [stockId]: { ...prev[stockId], restricted: excluded },
        }));
    };

    const handleSave = () => {
        const overridenStocks = Object.values(stockOverrides)
            .filter((stock) => !stock.restricted)
            .map((stock) => ({ shareClassId: stock.shareClassId, shareId: stock.shareId }));

        const restrictedStocks = Object.values(stockOverrides)
            .filter((stock) => stock.restricted)
            .map((stock) => ({ shareClassId: stock.shareClassId, shareId: stock.shareId }));

        onSave(selectedSubCategories, overridenStocks, restrictedStocks);
    };

    const handleCancel = () => {
        onClose();
    };

    const numberOfSelectedSubCategories = selectedSubCategories.length;
    const numberOfExcludedStocks = restrictedStockIds.length;
    const numberOfAllStocks = getAllStocksForCategory(category).length;
    const numberOfTradableStocks = Object.values(currentAffectedStocks).filter(
        (override) =>
            !override.restricted &&
            !preferences?.restrictions.some((clientRestriction) => clientRestriction.shareId === override.shareId),
    ).length;

    // Calculate market value for excluded stocks using the same method as the main tab
    const excludedMarketStats = useMemo(() => {
        let sumMarketCap = 0;
        let sumMarketShare = 0;

        restrictedStockIds.forEach((shareClassId) => {
            const stock = stocks.find((st) => st.shareClassId === shareClassId);
            if (stock) {
                sumMarketCap += parseFloat(stock.marketCap || '0');
                sumMarketShare += parseFloat(stock.marketCapWeight || '0');
            }
        });

        return {
            marketValue: formatCurrencyHuman(sumMarketCap, 'USD'),
            marketShare: formatPercent(sumMarketShare),
        };
    }, [stocks, restrictedStockIds]);

    const IconComponent = categoryIconMap[category.sriCategoryId as keyof typeof categoryIconMap] || LuCigarette;

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="flex h-[90vh] max-w-4xl flex-col">
                <TooltipProvider>
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-3">
                            <div
                                className="flex h-8 w-8 items-center justify-center rounded-full"
                                style={{ backgroundColor: 'rgba(125, 85, 43, 0.1)' }}
                            >
                                <IconComponent className="h-4 w-4" style={{ color: 'rgb(125, 85, 43)' }} />
                            </div>
                            Edit {category.sriCategoryLabel}
                        </DialogTitle>
                        <DialogDescription>
                            Configure which sub-categories and individual stocks to restrict for this SRI category.
                        </DialogDescription>
                    </DialogHeader>

                    <div className="flex flex-1 flex-col">
                        <Card className="mb-4">
                            <div className="-2 grid grid-cols-2 gap-4 p-2">
                                <div className="text-center">
                                    <div className="text-sm font-medium text-gray-700">Excluded Stocks</div>
                                    <div className="text-sm text-gray-600">
                                        {numberOfExcludedStocks} Excluded, {numberOfTradableStocks} Tradeable
                                    </div>
                                </div>
                                <div className="text-center">
                                    <div className="text-sm font-medium text-gray-700">Excluded Market Value</div>
                                    <div className="text-sm text-gray-600">
                                        {excludedMarketStats.marketValue}, {excludedMarketStats.marketShare} of NDVR
                                        1500
                                    </div>
                                </div>
                            </div>
                        </Card>
                        <Tabs defaultValue="selection" className="flex flex-1 flex-col">
                            <TabsList className="grid w-full grid-cols-2">
                                <TabsTrigger value="selection">
                                    Selection ({numberOfSelectedSubCategories} of {category.subCategories.length})
                                </TabsTrigger>
                                <TabsTrigger value="excluded-stocks">
                                    Excluded Stocks ({numberOfExcludedStocks} of {numberOfAllStocks})
                                </TabsTrigger>
                            </TabsList>

                            <div className="relative flex-1">
                                <TabsContent value="selection" className="absolute inset-0 overflow-auto">
                                    <SelectionTab
                                        category={category}
                                        selectedSubCategories={selectedSubCategories}
                                        onSelectionChange={setSelectedSubCategories}
                                    />
                                </TabsContent>

                                <TabsContent value="excluded-stocks" className="absolute inset-0">
                                    <ExcludedStocksTab
                                        affectedStocks={currentAffectedStocks}
                                        restrictedStockIds={restrictedStockIds}
                                        restrictions={preferences?.restrictions || []}
                                        onExclusionChange={handleStockOverrideChange}
                                    />
                                </TabsContent>
                            </div>
                        </Tabs>
                    </div>

                    <div className="flex justify-end gap-2 pt-4">
                        <Button variant="outline" onClick={handleCancel}>
                            Cancel
                        </Button>
                        <Button onClick={handleSave} disabled={isSaving}>
                            {isSaving && <Spinner size="sm" />}
                            Save
                        </Button>
                    </div>
                </TooltipProvider>
            </DialogContent>
        </Dialog>
    );
};
